export type ListPagesApiRow = {
  id: string;
  name: string;
  username?: string;
  picture?: string;
  linked_instagram_user_id?: string;
  linked_instagram_username?: string;
  instagram_business_account?: { id?: string; username?: string };
  is_eligible_for_branded_content?: boolean;
  last_synced_at?: string;
  page_sources?: ("promote_pages" | "me_accounts")[];
};

export type ListInstagramApiRow = {
  id: string;
  name?: string;
  username?: string;
  profile_picture_url?: string;
  last_synced_at?: string;
};

export type CatalogRefreshScheduleOutcome =
  | "emitted"
  | "emit_failed"
  | "skipped_debounce"
  | "skipped_in_flight"
  | "skipped_no_context"
  | "not_requested";

export type MetaIdentityCatalogSyncSummary = {
  last_synced_at?: string;
  page_count?: number;
  pages_written?: number;
  pages_failed?: number;
  instagram_count?: number;
  instagram_written?: number;
  instagram_failed?: number;
  catalog_complete?: boolean;
  refresh_outcome?: CatalogRefreshScheduleOutcome;
  sources?: {
    promote_pages?: "ok" | "failed";
    me_accounts?: "ok" | "failed";
  };
};

export type ListPagesApiResponse =
  | ListPagesApiRow[]
  | {
      pages?: ListPagesApiRow[];
      catalog_sync?: MetaIdentityCatalogSyncSummary;
    };

export type ListInstagramApiResponse =
  | ListInstagramApiRow[]
  | {
      instagram_accounts?: ListInstagramApiRow[];
      catalog_sync?: MetaIdentityCatalogSyncSummary;
    };

export function parseListPagesApiResponse(
  response: ListPagesApiResponse | null | undefined
): {
  pages: ListPagesApiRow[];
  catalog_sync?: MetaIdentityCatalogSyncSummary;
} {
  if (Array.isArray(response)) {
    return { pages: response };
  }
  return {
    pages: response?.pages ?? [],
    catalog_sync: response?.catalog_sync,
  };
}

export function parseListInstagramApiResponse(
  response: ListInstagramApiResponse | null | undefined
): {
  instagram_accounts: ListInstagramApiRow[];
  catalog_sync?: MetaIdentityCatalogSyncSummary;
} {
  if (Array.isArray(response)) {
    return { instagram_accounts: response };
  }
  return {
    instagram_accounts: response?.instagram_accounts ?? [],
    catalog_sync: response?.catalog_sync,
  };
}

function catalogSyncTimestampMs(
  sync?: MetaIdentityCatalogSyncSummary
): number {
  const syncedAt = sync?.last_synced_at?.trim();
  if (!syncedAt) {
    return 0;
  }
  const parsed = Date.parse(syncedAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function catalogSyncAdvanced(
  prior?: MetaIdentityCatalogSyncSummary,
  current?: MetaIdentityCatalogSyncSummary
): boolean {
  const priorMs = catalogSyncTimestampMs(prior);
  const currentMs = catalogSyncTimestampMs(current);
  if (!currentMs) {
    return false;
  }
  if (!priorMs) {
    return true;
  }
  return currentMs > priorMs;
}

export function isSkippedCatalogRefreshOutcome(
  outcome?: CatalogRefreshScheduleOutcome
): outcome is "skipped_debounce" | "skipped_in_flight" {
  return outcome === "skipped_debounce" || outcome === "skipped_in_flight";
}

export function isCatalogRefreshInFlight(
  outcome?: CatalogRefreshScheduleOutcome
): boolean {
  return outcome === "emitted" || outcome === "skipped_in_flight";
}

/** Poll only when cache is empty or a background refresh is actually running. */
export function shouldPollIdentityCatalogAfterRead(args: {
  rowCount: number;
  catalog_sync?: MetaIdentityCatalogSyncSummary;
  forceSync?: boolean;
  awaitCombinedCatalogComplete?: boolean;
}): boolean {
  if (args.forceSync) {
    return true;
  }
  if (args.catalog_sync?.catalog_complete) {
    return false;
  }
  if (args.awaitCombinedCatalogComplete) {
    return true;
  }
  if (args.rowCount === 0) {
    return isCatalogRefreshInFlight(args.catalog_sync?.refresh_outcome);
  }
  return isCatalogRefreshInFlight(args.catalog_sync?.refresh_outcome);
}

export function catalogRefreshOutcomeMessage(
  outcome: CatalogRefreshScheduleOutcome
): string | undefined {
  if (outcome === "skipped_in_flight") {
    return "A Meta catalog sync is already running. Options will update when it finishes.";
  }
  if (outcome === "skipped_debounce") {
    return "Catalog was synced recently. Try again in a few minutes.";
  }
  if (outcome === "emit_failed") {
    return "Could not schedule a Meta catalog sync. Try again or contact support.";
  }
  return undefined;
}

/** Fill missing page avatars from linked IG rows (and vice versa) after cache read. */
export function enrichIdentityCatalogPictures<
  TPage extends {
    id: string;
    picture?: string;
    linked_instagram_user_id?: string;
  },
  TIg extends { id: string; profile_picture_url?: string },
>(pages: readonly TPage[], instagramAccounts: readonly TIg[]): {
  pages: TPage[];
  instagramAccounts: TIg[];
} {
  const igById = new Map(
    instagramAccounts.map((ig) => [ig.id, ig] as const)
  );
  const enrichedPages = pages.map((page) => {
    if (page.picture?.trim()) {
      return page;
    }
    const linkedId = page.linked_instagram_user_id?.trim();
    const igPicture = linkedId
      ? igById.get(linkedId)?.profile_picture_url?.trim()
      : undefined;
    return igPicture ? { ...page, picture: igPicture } : page;
  });
  const pageByLinkedIg = new Map<string, string>();
  for (const page of enrichedPages) {
    const linkedId = page.linked_instagram_user_id?.trim();
    const picture = page.picture?.trim();
    if (linkedId && picture) {
      pageByLinkedIg.set(linkedId, picture);
    }
  }
  const enrichedIg = instagramAccounts.map((account) => {
    if (account.profile_picture_url?.trim()) {
      return account;
    }
    const pagePicture = pageByLinkedIg.get(account.id);
    return pagePicture
      ? { ...account, profile_picture_url: pagePicture }
      : account;
  });
  return { pages: enrichedPages, instagramAccounts: enrichedIg };
}
